import {
  checkRegistrationEnabled,
  deleteWebinar,
  fetchCountries,
  fetchMyQuestions,
  fetchPaginatedAttendees,
  fetchProfileInfo,
  fetchQuestionWithReplies,
  fetchRoomData,
  fetchWebinarAttendees,
  fetchWebinarAttendeesFilters,
  postWebinarAttendeeAction,
  postWebinarAttendeeActionByFilter,
  postWebinarDeclineInvitation,
  questionAndAnswerFilter,
  fetchCTAs,
} from '@src/api/endpoints/webinar';

import { queryKeys } from '@src/utils/queryKeys';

import {
  TSortFilter,
  TShowFilter,
  IAttendeesFilters,
  IScheduledMeeting,
  IQuestionResponse,
  IQuestionAndAnswerFilterResponse,
  IFetchCTAsResponse,
} from '@src/types';
import { IWebinarAttendees } from '@src/webinar/types';
import { useInfiniteQuery, useMutation, useQuery } from '@tanstack/react-query';
import { fetchAllMeetingsAndWebinars } from '@src/api/endpoints/webinar';

export const useRoomQuery = (roomId: string | null) =>
  useQuery({
    queryKey: ['room', roomId],
    queryFn: () => fetchRoomData(roomId!),
    enabled: !!roomId,
  });

export const useRegistrationCheck = (roomId: string | null) =>
  useQuery({
    queryKey: ['registrationCheck', roomId],
    queryFn: () => checkRegistrationEnabled(roomId!),
    enabled: !!roomId,
    staleTime: Infinity,
    retry: false,
  });

export const useCountries = () =>
  useQuery({
    queryKey: ['countries'],
    queryFn: fetchCountries,
    staleTime: Infinity,
  });

export const useWebinarAttendeesQuery = (id?: string) =>
  useQuery<IWebinarAttendees>({
    queryKey: [queryKeys.webinar.attendees(id!)],
    queryFn: () => fetchWebinarAttendees(id!),
    select: (data): IWebinarAttendees => data ?? ({} as IWebinarAttendees),
    // staleTime: 10 * 1000 * 60,
    enabled: !!id,
  });

export const useWebAttendeesTableQuery = (id: string, filters?: IAttendeesFilters) =>
  useInfiniteQuery({
    queryKey: [queryKeys.webinar.attendeesPaginated(id), filters],
    initialPageParam: 1,
    // staleTime: 30000, // 30 seconds
    getNextPageParam: (lastPage: any) => lastPage?.nextPage,
    queryFn: ({ pageParam }) => fetchPaginatedAttendees(id, pageParam, filters),
    enabled: !!id,
  });

export const useFilteredWebinarAttendees = (roomId?: string) =>
  useQuery({
    queryKey: [queryKeys.webinar.attendeesFilters],
    queryFn: () => fetchWebinarAttendeesFilters(roomId!),
    enabled: !!roomId,
    staleTime: 10 * 1000 * 60,
    retry: false,
  });

export const usePostWebinarAttendeeAction = () =>
  useMutation({
    mutationFn: postWebinarAttendeeAction,
  });

export const usePostWebinarAttendeeActionByFilter = () =>
  useMutation({
    mutationFn: postWebinarAttendeeActionByFilter,
  });

export const usePostWebinarDeclineInvitation = () =>
  useMutation({
    mutationFn: postWebinarDeclineInvitation,
  });

export const useFetchProfileInfo = () =>
  useQuery({
    queryKey: [queryKeys.profileInfo.root],
    queryFn: () => fetchProfileInfo(),
    staleTime: 10 * 1000 * 60,
    retry: false,
  });

export const useDeleteWebinar = () => {
  return useMutation({
    mutationFn: (id: string) => deleteWebinar(id),
  });
};

export const useFetchWebinarMeeting = () => {
  return useQuery<IScheduledMeeting[], Error>({
    queryKey: [queryKeys.webinar.listMeeting],
    queryFn: fetchAllMeetingsAndWebinars,
  });
};

export const useQuestionAndAnswerFilter = (roomId: string, filterType: TShowFilter, sortType: TSortFilter) => {
  return useQuery<IQuestionAndAnswerFilterResponse>({
    queryKey: [queryKeys.webinar.questions, filterType, sortType],
    queryFn: () => questionAndAnswerFilter(roomId, filterType, sortType),
  });
};

export const useQuestionReplies = (roomId: string, questionId: string, isEnabled: boolean) => {
  return useQuery<IQuestionResponse>({
    queryKey: [queryKeys.webinar.questionReply(questionId), roomId],
    queryFn: () => fetchQuestionWithReplies(roomId, questionId),
    enabled: isEnabled && !!questionId,
    retry: false,
  });
};

export const useMyQuestions = (roomId: string, enabled: boolean) => {
  return useQuery<string[]>({
    queryKey: [queryKeys.webinar.myQuestions],
    queryFn: () => fetchMyQuestions(roomId),
    enabled,
  });
};

// TODO: change roomId to be required after BE implements create CTA's before creating a webinar
// `enabled` is exposed so consumers can do "fetch on click" via `refetch()` as temporary workaround
export const useFetchCTAs = (roomId?: string, enabled = true) =>
  useQuery<IFetchCTAsResponse>({
    queryKey: [queryKeys.webinar.ctas(roomId!)],
    queryFn: () => fetchCTAs(roomId!),
    enabled: enabled && !!roomId,
  });
